import { useState, useEffect, useRef, useCallback } from "react";
import API_BASE_URL from "../config";
import { SEARCH_DEBOUNCE_MS as DEFAULT_DEBOUNCE } from "./useDebouncedValue";

/**
 * Debounced, abortable hybrid-search hook. Re-runs when query, types, documentIds
 * or database change. A newer request aborts the prior in-flight one so a stale
 * response can never overwrite newer state. Below `minChars` it clears results.
 * status ∈ idle | loading | empty | error | done.
 */
export default function useDebouncedSearch({
  database, types = null, documentIds = null, documentCategory = null, facets = null,
  debounceMs = DEFAULT_DEBOUNCE, minChars = 2,
} = {}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const run = useCallback(async (q) => {
    const trimmed = (q || "").trim();
    if (trimmed.length < minChars) {
      setResults([]); setCounts({}); setStatus("idle"); setError(null);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStatus("loading"); setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/knowledge/search`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        signal: ctrl.signal,
        body: JSON.stringify({ query: trimmed, database, types, document_ids: documentIds,
                               document_category: documentCategory, facets }),
      });
      if (!res.ok) throw new Error((await res.text()) || res.statusText);
      const data = await res.json();
      const list = Array.isArray(data.results) ? data.results : [];
      setResults(list); setCounts(data.counts || {});
      setStatus(list.length ? "done" : "empty");
    } catch (e) {
      if (e.name === "AbortError") return;   // superseded by a newer request
      setError(e.message); setStatus("error");
    }
  }, [database, types, documentIds, documentCategory, facets, minChars]);

  useEffect(() => {
    const id = setTimeout(() => run(query), debounceMs);
    return () => clearTimeout(id);
  }, [query, run, debounceMs]);

  return { query, setQuery, results, counts, status, error, run };
}
